import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";

import { analytics } from "services/analytics";
import { usePermissions } from "components/Hook";
import Layout from "components/Screens/LoginV2/Layout/Layout";
import SetPassword from "components/common/SetPasswordPage";
import PageLoader from "components/common/PageLoader";
import Button from "components/common/Button";
import CustomAlert from "components/common/Alert";

import { AuthUser, ResetAuthUser } from "store/actions/AuthUser";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";

import { baseUrl, s3ImagesBaseURL } from "util/APIBaseUrl";
import { createPasswordApi, validateCustomerOnboardingTokenApi } from "services/customer";
import { settingUserInLocalStorage } from "components/Screens/CustomerOnboarding/helpers";

import { getUserInfoRequest, handlePostLoginNavigation } from "components/Screens/LoginV2/Login/utils";
import { getLayoutImageProps } from "components/Screens/LoginV2/Layout/utils";
import { getStorageItem } from "util/utils";
import { getAuthUserData, getAuthUserLodaing } from "selectors/auth";
import { getLoadingAdditionalInfo, getUserAdditionalInfo } from "selectors/user";

import { VALIDATE_TOKEN_CASES } from "components/Screens/CustomerOnboarding/constants";
import { EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE, LOGIN_ROUTE } from "constants/routes";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import "./ResetPassword.scss";

function ResetPassword() {
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("token");
  const eventParam = queryParams.get("event");

  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { accessToken } = usePermissions();
  const [userInfo, setUserInfo] = useState({});
  const { integratedContractSigning } = useFlags();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authUserData = useSelector(getAuthUserData);
  const isLoadingAuthUser = useSelector(getAuthUserLodaing);
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const isLoadingAdditionalInfo = useSelector(getLoadingAdditionalInfo);
  const [isLoading, setIsLoading] = useState(true);
  const [resetSuccessScreen, setResetSuccessScreen] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const isForgotPasswordFlow = eventParam !== "Activation";

  const validateToken = async () => {
    const { code, success, message, ...rest } = await validateCustomerOnboardingTokenApi({ token });
    setIsLoading(false);
    if (success === false && code === VALIDATE_TOKEN_CASES.USED) {
      navigateTo(LOGIN_ROUTE, {
        state: {
          onboardingError: VALIDATE_TOKEN_CASES.USED,
        },
      });
      return;
    }
    if (success === false && code === VALIDATE_TOKEN_CASES.INVALID) {
      navigateTo(LOGIN_ROUTE, {
        state: {
          onboardingError: VALIDATE_TOKEN_CASES.INVALID,
        },
      });
      return;
    }
    if (success === false && code === VALIDATE_TOKEN_CASES.EXPIRED) {
      navigateTo(`${EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE}?event=${eventParam}&token=${token}&tokenStatus=expired`);
      return;
    }

    if (success === false && code === VALIDATE_TOKEN_CASES.INACTIVE_USER) {
      navigateTo(LOGIN_ROUTE, {
        state: {
          onboardingError: VALIDATE_TOKEN_CASES.INACTIVE_USER,
          errorMessage: message,
        },
      });
      return;
    }

    setUserInfo({ ...rest });
  };

  useEffect(() => {
    if (token) {
      const redirectUser = () => {
        if (accessToken) {
          localStorage.clear();
          dispatch(ResetAuthUser({}));
        }
        validateToken();
      };

      redirectUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { email, userType = "", firstName = "", lastName = "" } = userInfo;

  useEffect(() => {
    const loggedInUser = getStorageItem({ key: "loggedInUser", parse: true });
    const accessTokenFromLocalStorage = getStorageItem({ key: "accessToken" });

    if (accessTokenFromLocalStorage && loggedInUser) {
      const request = getUserInfoRequest(loggedInUser);
      if (request) {
        dispatch(UserAdditionalInfo(request));
      }
    }
  }, [authUserData, dispatch]);

  useEffect(() => {
    const loggedInUser = getStorageItem({ key: "loggedInUser", parse: true });
    const accessTokenFromLocalStorage = getStorageItem({ key: "accessToken" });

    if (userAdditionalInfo && Object.keys(userAdditionalInfo).length && accessTokenFromLocalStorage && loggedInUser) {
      // Analytics tracking
      analytics.track("Logged In", {
        channel: "password",
        name: loggedInUser?.name,
        email: loggedInUser?.email,
        userID: loggedInUser?.userId,
        accountType: loggedInUser?.type,
      });

      // Deep-link restore is a login-flow concern, not a password-reset one; the campaign params
      // merge still applies.
      handlePostLoginNavigation(loggedInUser, userAdditionalInfo, navigateTo, integratedContractSigning, {
        restoreIntendedRoute: false,
      });
    }
  }, [userAdditionalInfo, navigateTo]);

  const resetPasswordHandler = async (data = {}) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { newPassword: newPasswordFromData, confirmPassword } = data;
    const payload = {
      password: newPasswordFromData,
      confirmPassword,
      token,
    };
    setNewPassword(newPasswordFromData);
    const response = await createPasswordApi(payload);
    const { accessToken: accessTokenFromResponse = "", refreshToken = "" } = response;

    if (!accessTokenFromResponse) {
      setIsSubmitting(false);
      return;
    }
    settingUserInLocalStorage(accessTokenFromResponse, refreshToken);
    if (isForgotPasswordFlow) {
      setIsSubmitting(false);
      setResetSuccessScreen(true);
      return;
    }

    LoggingInUser(newPasswordFromData);
    setIsSubmitting(false);
  };

  const LoggingInUser = (passwordParam = newPassword) => {
    dispatch(
      AuthUser({
        userCredential: {
          password: passwordParam,
          email,
          type: userType,
        },
        request: `${baseUrl}/auth/${userType.toLowerCase()}`,
        loginPortal: userType,
      })
    );
  };

  // Employee activation keeps its own onboarding artwork; every other reset
  // follows the user type, so a customer stays on the customer visual.
  const activationOrUserTypeImageProps = isForgotPasswordFlow
    ? getLayoutImageProps(userType)
    : { rightSideImage: `${s3ImagesBaseURL}/employee-onboarding.png` };

  return (
    <Layout
      backgroundColor="var(--color-primitives-white-white)"
      rightSideImage={`${s3ImagesBaseURL}/login-image.jpg`}
      contentContainerClass="reset-password-layout-container"
      {...activationOrUserTypeImageProps}
    >
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="reset-password-content">
          <div className="flex flex-col justify-center align-self-center h-full">
            {resetSuccessScreen ? (
              <>
                <div className="heading-h1-semibold">Success!</div>
                <div className="mt-1 mb-1">
                  <CustomAlert
                    severity="success"
                    variant="standard"
                    className="success-alert-container"
                    icon={<CheckCircleOutlineIcon className="alert-icon-color" />}
                    description={
                      <div className=" para-body-m-regular color-text-success w-100p">
                        Your password has been changed and we have logged you in!
                      </div>
                    }
                  />
                </div>

                <Button
                  id="continue"
                  className="continue-button"
                  variant="contained"
                  label="Continue"
                  size="large"
                  isLoading={isLoadingAuthUser}
                  onClick={() => LoggingInUser()}
                />
              </>
            ) : (
              <SetPassword
                isForgotPasswordFlow={isForgotPasswordFlow}
                userName={`${firstName} ${lastName}`}
                email={email}
                resetPasswordHandler={resetPasswordHandler}
                isSubmitting={isSubmitting || isLoadingAuthUser || isLoadingAdditionalInfo}
                resetPasswordWrapperHeightClass=""
                showTermsAndPolicy={false}
                userType={userType}
              />
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ResetPassword;
